import {
  Accordion,
  Box,
  Button,
  StarRating,
  Text,
  Link,
  useMediaQuery,
  HorizontalGauge,
  Tabs,
  Image as ImageComponent,
} from '@lounge-fe/ui-kit'
import { useReviewsQuery } from './useReviewsQuery'
import Review from './Review'
import {
  AttributeRating,
  ReviewsResponse,
  type Review as IReview,
} from './types'
import { ReviewsProps } from './types'
import { ReviewsModal } from './ReviewsModal'
import { t } from 'i18next'
import { ComponentProps, ReactNode, useMemo, useState } from 'react'
import { Trigger } from '@lounge-fe/ui-kit/src/components/Popover/Trigger'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { ReviewsSummary } from './ReviewsSummary'

const ReviewList = ({ reviews }: { reviews: IReview[] }) => {
  const [showOriginal, setShowOriginal] = useState<Record<number, boolean>>({})

  const handleToggleOriginal = (id: number) => {
    return () => {
      setShowOriginal((prev) => ({
        ...prev,
        [id]: !prev[id],
      }))
    }
  }

  return (
    <>
      {reviews.map(
        ({
          reviewer: { author, isVerified },
          title,
          dateCreated,
          rating,
          body,
          id,
          original,
        }) => (
          <Review key={id}>
            <Review.Details
              rating={rating}
              title={showOriginal[id] && original ? original.title : title}
              verified={isVerified}
            />
            <Review.Description>
              {showOriginal[id] && original ? original.body : body}
            </Review.Description>
            <Review.Cite
              date={dateCreated}
              author={author}
              {...(original && {
                original,
                showOriginal: showOriginal[id],
                onToggleOriginal: handleToggleOriginal(id),
              })}
            />
          </Review>
        ),
      )}
    </>
  )
}

const AttributeRatings = ({
  attributeRatings,
}: {
  attributeRatings: AttributeRating[]
}) => {
  return (
    <div>
      {attributeRatings
        ?.filter((attribute) => attribute.type === 'centered-range')
        .map((attribute, index, filteredAttributes) => {
          const isLast = index === (filteredAttributes.length ?? 0) - 1
          return (
            <div key={attribute.title} className={isLast ? '' : 'mb-6'}>
              <Text variant="title-six" className="mb-2">
                {attribute.title}
              </Text>
              <HorizontalGauge>
                <HorizontalGauge.Bar
                  className="border-0 bg-[#E7E7E7]"
                  label={attribute.minLabel}
                  labelAlignment="left"
                />
                <HorizontalGauge.Bar
                  className="border-0 bg-[#E7E7E7]"
                  label={attribute.midLabel}
                  labelAlignment="center"
                />
                <HorizontalGauge.Bar
                  className="border-0 bg-[#E7E7E7]"
                  label={attribute.maxLabel}
                  labelAlignment="right"
                />
                <HorizontalGauge.Indicator
                  value={attribute.value}
                  snapToPoints={true}
                  snapPoints={11}
                />
              </HorizontalGauge>
            </div>
          )
        })}
    </div>
  )
}

const Reviews = ({
  productId,
  onClick,
  isInitialOpen,
  variant = 'accordion',
  avgRating,
  reviewCount,
  productImage,
}: ReviewsProps) => {
  const isDesktop = useMediaQuery(
    ({ screens }) => `(min-width: ${screens['md']})`,
  )

  const { getFlag } = useFeatureFlags()

  const isProductReviewsV2Enabled = getFlag('enable_product_reviews_v2')

  const isAiReviewSummaryEnabled = getFlag('ai_review_summary')

  const {
    data: { pages = [] } = {},
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useReviewsQuery({
    productId,
    targetPageSize: 24,
    getNextPageParam: (lastPage: ReviewsResponse) => lastPage.cursor,
    refetchOnWindowFocus: false,
    enabled: productId !== undefined,
  })

  const reviews = useMemo(() => {
    return pages.reduce<IReview[]>((acc, { reviews = [] }) => {
      return [...acc, ...reviews]
    }, [])
  }, [pages])

  const attributeRatings = useMemo(() => {
    return pages[0]?.attributeRatings
  }, [pages])

  const summary = useMemo(() => {
    return pages[0]?.summary
  }, [pages])

  const averageRating = useMemo(() => {
    return avgRating ?? 0
  }, [avgRating, reviews])

  if (averageRating === 0) {
    return null
  }

  const totalReviews = reviewCount

  const accordionTrigger: ComponentProps<typeof Trigger>['children'] = (
    <Link
      as="button"
      className="w-full mt-2 mb-4 underline typography--body-sm text-left"
    >
      {t('product.reviews.read_more')}
    </Link>
  )

  const productInfoTrigger: ComponentProps<typeof Trigger>['children'] = (
    <Link
      as="button"
      className="flex flex-row align-center items-center gap-2 mb-4 mt-4"
    >
      {averageRating !== undefined && (
        <StarRating maxStars={5} stars={averageRating} />
      )}{' '}
      <Text variant="body-sm" className="underline">
        {totalReviews} {t('general.reviews')}
      </Text>
    </Link>
  )

  if (variant === 'productInfo') {
    if (isProductReviewsV2Enabled) {
      return (
        <ReviewsModal trigger={productInfoTrigger}>
          <div className="flex flex-1 flex-col gap-8 min-h-0 md:flex-row md:max-h-[70vh]">
            {productImage && isDesktop && (
              <div className="aspect-[2/3] md:basis-[40%] md:h-full">
                <ImageComponent
                  className="w-full h-full object-cover object-center"
                  data={{
                    src: productImage?.src,
                    width: 343,
                    height: 516,
                  }}
                  alt={productImage?.alt}
                />
              </div>
            )}
            <div className="flex flex-1 flex-col min-h-0 md:basis-[60%] md:h-full">
              <div className="flex-shrink-0">
                <div className="flex items-start justify-between gap-4">
                  <Text variant="title-four" className="text-left">
                    {t('product.reviews.customer_reviews')}
                  </Text>
                </div>
                <div className="flex flex-col gap-2 mt-2">
                  {averageRating !== undefined && (
                    <div className="flex flex-row items-center gap-2">
                      <Text variant="title-four">{averageRating}</Text>
                      <StarRating maxStars={5} stars={averageRating} />
                    </div>
                  )}
                  <Text variant="body-xs" className="text-subtle">
                    {t('product.reviews.based_on_x_reviews', {
                      count: totalReviews,
                    })}
                  </Text>
                </div>
              </div>

              {attributeRatings && attributeRatings.length > 0 ? (
                <div className="flex-1 min-h-0 mt-4">
                  <Tabs id="reviews-tabs">
                    <div className="flex h-full flex-col">
                      <div className="flex-shrink-0">
                        <Tabs.Header>
                          <Tabs.Tab>
                            {t('product.reviews.product_fit')}
                          </Tabs.Tab>
                          <Tabs.Tab>
                            {t('product.reviews.customer_reviews')}
                          </Tabs.Tab>
                        </Tabs.Header>
                      </div>
                      <Tabs.Panels className="flex-1 min-h-0 overflow-y-auto">
                        <Tabs.Panel
                          className="h-full"
                          forwardButtonLabel={
                            !isDesktop
                              ? t('product.reviews.view_reviews')
                              : undefined
                          }
                        >
                          <div className="pr-2 py-8 md:py-6">
                            {isAiReviewSummaryEnabled && summary && (
                              <ReviewsSummary summary={summary} />
                            )}
                            {attributeRatings && (
                              <AttributeRatings
                                attributeRatings={attributeRatings}
                              />
                            )}
                          </div>
                        </Tabs.Panel>
                        <Tabs.Panel className="h-full">
                          <div className="pr-2 py-8 md:py-6">
                            <ReviewList reviews={reviews} />
                            <Box className="text-center my-6">
                              <Text variant="body-sm" className="mb-2">
                                {t('product.reviews.showing', {
                                  count: reviews.length,
                                  total: totalReviews,
                                })}
                              </Text>
                              {hasNextPage && (
                                <Button
                                  onClick={() => fetchNextPage()}
                                  isLoading={isFetching}
                                >
                                  {t('action.load_more')}
                                </Button>
                              )}
                            </Box>
                          </div>
                        </Tabs.Panel>
                      </Tabs.Panels>
                    </div>
                  </Tabs>
                </div>
              ) : (
                <div className="mt-4 pr-2 pb-8 md:pb-6 overflow-y-auto">
                  <ReviewList reviews={reviews} />
                  <Box className="text-center my-6">
                    <Text variant="body-sm" className="mb-2">
                      {t('product.reviews.showing', {
                        count: reviews.length,
                        total: totalReviews,
                      })}
                    </Text>
                    {hasNextPage && (
                      <Button
                        onClick={() => fetchNextPage()}
                        isLoading={isFetching}
                      >
                        {t('action.load_more')}
                      </Button>
                    )}
                  </Box>
                </div>
              )}
            </div>
          </div>
        </ReviewsModal>
      )
    } else {
      return (
        <ReviewsModal trigger={productInfoTrigger}>
          <ReviewList reviews={reviews} />
          <Box className="text-center my-6">
            <Text variant="body-sm" className="mb-2">
              {t('product.reviews.showing', {
                count: reviews.length,
                total: totalReviews,
              })}
            </Text>
            {hasNextPage && (
              <Button onClick={() => fetchNextPage()} isLoading={isFetching}>
                {t('action.load_more')}
              </Button>
            )}
          </Box>
        </ReviewsModal>
      )
    }
  }

  return (
    <Accordion.Item
      onClick={onClick}
      initialOpen={isInitialOpen}
      title={
        <div className="flex justify-start gap-3 w-full">
          <Text variant="title-six">
            {t('general.reviews')} ({reviewCount})
          </Text>
          {averageRating && !isDesktop && (
            <StarRating maxStars={5} stars={averageRating} />
          )}
        </div>
      }
    >
      <ReviewList reviews={reviews.slice(0, 2)} />

      {(hasNextPage || reviews.length > 0) && (
        <ReviewsModal trigger={accordionTrigger}>
          <ReviewList reviews={reviews} />
          <Box className="text-center my-6">
            <Text variant="body-sm" className="mb-2">
              {t('product.reviews.showing', {
                count: reviews.length,
                total: reviewCount,
              })}
            </Text>
            {hasNextPage && (
              <Button onClick={() => fetchNextPage()} isLoading={isFetching}>
                {t('action.load_more')}
              </Button>
            )}
          </Box>
        </ReviewsModal>
      )}
    </Accordion.Item>
  )
}

export { Reviews }
